import { useState } from 'react'

import { Botao, Campo, Cartao, classesEntrada } from '@/componentes/ui'
import { confirmarCodigo, enviarCodigo } from '@/lib/supabase'
import { TAMANHO_CODIGO_OTP } from '@/tipos/dominio'

/**
 * Login por código de 6 dígitos no e-mail.
 *
 * Sem senha de propósito: o público é feminino, não técnico, e vai usar
 * o app todo dia por 28 dias. Senha esquecida é o maior ponto de perda
 * de acesso nesse cenário, e "recuperar senha" no meio do programa é
 * exatamente o atrito que derruba a adesão.
 */
export function PaginaEntrar({ aoEntrar }: { aoEntrar: () => void }) {
  const [etapa, setEtapa] = useState<'email' | 'codigo'>('email')
  const [email, setEmail] = useState('')
  const [codigo, setCodigo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  async function pedirCodigo(evento: React.FormEvent) {
    evento.preventDefault()
    setErro(null)
    setEnviando(true)
    try {
      await enviarCodigo(email)
      setEtapa('codigo')
    } catch {
      setErro('Não consegui enviar o código. Confira o e-mail e tente de novo.')
    } finally {
      setEnviando(false)
    }
  }

  async function validarCodigo(evento: React.FormEvent) {
    evento.preventDefault()
    setErro(null)
    setEnviando(true)
    try {
      await confirmarCodigo(email, codigo)
      aoEntrar()
    } catch {
      setErro('Código inválido ou expirado. Peça um novo.')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-6 px-5 py-10">
      <header className="text-center">
        <span className="text-5xl" aria-hidden>
          🌺
        </span>
        <h1 className="mt-3 font-titulo text-2xl font-bold">Projeto Viver</h1>
        <p className="mt-1 text-[15px] text-[var(--tinta-suave)]">
          28 dias para desinflamar, recuperar energia e viver melhor.
        </p>
      </header>

      <Cartao>
        {etapa === 'email' ? (
          <form onSubmit={pedirCodigo} className="flex flex-col gap-4">
            <Campo
              rotulo="Seu e-mail"
              erro={erro ?? undefined}
              dica={`Você vai receber um código de ${TAMANHO_CODIGO_OTP} dígitos. Sem senha.`}
            >
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="voce@email.com"
                className={classesEntrada}
              />
            </Campo>

            <Botao type="submit" tamanho="bloco" carregando={enviando}>
              Receber meu código
            </Botao>
          </form>
        ) : (
          <form onSubmit={validarCodigo} className="flex flex-col gap-4">
            <Campo
              rotulo={`Código de ${TAMANHO_CODIGO_OTP} dígitos`}
              erro={erro ?? undefined}
              dica={`Enviado para ${email}. Confira também o spam.`}
            >
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                // Aceita alguns dígitos a mais do que o esperado: um campo
                // mais curto que o código gerado trunca a entrada em
                // silêncio, e a participante vê "código inválido" com o
                // código certo em mãos.
                maxLength={TAMANHO_CODIGO_OTP + 2}
                autoFocus
                required
                value={codigo}
                onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))}
                placeholder={'0'.repeat(TAMANHO_CODIGO_OTP)}
                className={`${classesEntrada} text-center font-titulo text-2xl tracking-[0.3em]`}
              />
            </Campo>

            <Botao
              type="submit"
              tamanho="bloco"
              carregando={enviando}
              disabled={codigo.length < TAMANHO_CODIGO_OTP}
            >
              Entrar
            </Botao>

            <Botao
              variante="fantasma"
              tamanho="pequeno"
              onClick={() => {
                setEtapa('email')
                setCodigo('')
                setErro(null)
              }}
            >
              Usar outro e-mail
            </Botao>
          </form>
        )}
      </Cartao>
    </main>
  )
}
